<template>
  <div class="row rs">
    <!-- Live dials: memory, requests per second, disk -->
    <div class="col-12 g">
      <div class="cluster">
        <div class="cluster-head">
          <h5 class="mb-0">السيرفر الآن</h5>
          <span class="live" :class="{ on: live && live.fresh }"><i></i>{{ live && live.fresh ? 'مباشر · كل ثانيتين' : 'متوسط آخر 10 ثوانٍ' }}</span>
          <div v-if="live" class="cpu" :title="'متوسط الحمل ' + live.load + ' · ' + live.cpus + ' نواة'">
            <span class="cpu-label">المعالج</span>
            <div class="cpu-bar" role="progressbar" :aria-valuenow="cpuPct" aria-valuemin="0" aria-valuemax="100" aria-label="استهلاك المعالج">
              <span :class="cpuPct >= 85 ? 'hot' : cpuPct >= 70 ? 'warm' : ''" :style="{ width: cpuPct + '%' }"></span>
            </div>
            <bdi dir="ltr" class="num cpu-val">{{ cpuPct }}%</bdi>
            <small class="text-muted"><bdi class="num">{{ live.cpus }}</bdi> نواة · <bdi class="num">{{ live.processes }}</bdi> عملية</small>
          </div>
        </div>
        <div class="dials">
          <rs-gauge class="dial-side" :style="tilt(1)" :value="ramPct" :max="100" :step="20" :red="85"
                    unit="الرامات %" :odo="live ? gb(live.ram.used) : '—'"
                    :sub="live ? 'جيجا مستخدمة من ' + gb(live.ram.total) : ''" :mood="live ? ramMood : ''" />
          <rs-gauge class="dial-main" :value="live ? live.rps : 0" :max="120" :step="20" :red="90" :peak="peak"
                    unit="طلب / ثانية" :odo="live ? pad(live.today, 7) : '———————'"
                    :sub="'عدد طلبات اليوم'" :mood="live ? rpsMood + ' · الآن ' + rpsText(live.rps) + ' · الأعلى ' + rpsText(peak) : 'تشغيل المحرك...'" />
          <rs-gauge class="dial-side" :style="tilt(-1)" :value="diskPct" :max="100" :step="20" :red="85"
                    unit="مساحة السيرفر %" :odo="live && live.disk ? pad(Math.round(live.disk.used / 1073741824), 4) : '—'"
                    :sub="live && live.disk ? 'جيجا مستخدمة من ' + gb(live.disk.used + live.disk.free) : ''" :mood="live && live.disk ? diskMood + ' · فاضي ' + gb(live.disk.free) + ' جيجا' : ''" />
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="col-12 g">
      <div class="card card-body mb-0">
        <div class="d-flex flex-wrap justify-content-between align-items-start head">
          <div>
            <h4 class="mb-25"><i class="fa fa-line-chart"></i> إحصائيات الطلبات</h4>
            <p class="text-muted mb-0">
              كل طلب يصل للسيرفر (نفس سطور سجل pm2): المسار، والجهة، والمدة، والحجم. تُجمع في الذاكرة وتُحفظ كل 10 ثوانٍ، وتُحذف تلقائياً بعد 30 يوماً.
            </p>
          </div>
          <div class="d-flex align-items-center flex-wrap tools">
            <div class="custom-control custom-switch">
              <input type="checkbox" class="custom-control-input" id="rs-auto" v-model="auto">
              <label class="custom-control-label" for="rs-auto">تحديث كل دقيقة</label>
            </div>
            <button class="btn btn-sm btn-primary" @click="load()" :disabled="loading">
              <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i> تحديث
            </button>
          </div>
        </div>
        <div class="d-flex flex-wrap align-items-end mt-1 filters">
          <div class="btn-group flex-wrap" role="group" aria-label="الفترة">
            <button v-for="r in ranges" :key="r.key" class="btn btn-sm" :class="range == r.key ? 'btn-primary' : 'btn-outline-primary'" @click="setRange(r.key)">{{ r.label }}</button>
          </div>
          <div v-if="range == 'custom'" class="d-flex align-items-end dates">
            <div>
              <label for="rs-from" class="mb-0"><small>من</small></label>
              <input id="rs-from" type="date" class="form-control form-control-sm" v-model="from" :min="minDate" :max="today" @change="load()">
            </div>
            <div>
              <label for="rs-to" class="mb-0"><small>إلى</small></label>
              <input id="rs-to" type="date" class="form-control form-control-sm" v-model="to" :min="from || minDate" :max="today" @change="load()">
            </div>
          </div>
          <div>
            <label for="rs-side" class="mb-0"><small>الجهة</small></label>
            <select id="rs-side" class="form-control form-control-sm" v-model="side" @change="load()">
              <option value="">كل الجهات</option>
              <option v-for="(v, k) in sides" :key="k" :value="k">{{ v }}</option>
            </select>
          </div>
          <span v-if="focus" class="badge badge-light-primary focus">
            المسار: <bdi dir="ltr" class="mono">{{ focus.m }} {{ focus.p }}</bdi>
            <button class="btn btn-link btn-sm p-0" @click="setFocus(null)" title="إلغاء تحديد المسار" aria-label="إلغاء تحديد المسار"><i class="fa fa-times"></i></button>
          </span>
        </div>
        <small v-if="data" class="text-muted mt-50 d-block">
          الفترة: <bdi class="num">{{ data.from.slice(0, 16) }}</bdi> إلى <bdi class="num">{{ data.to.slice(0, 16) }}</bdi> (بتوقيت الرياض)
          <span v-if="updatedAt"> · آخر تحديث {{ updatedAt }}</span>
        </small>
      </div>
    </div>

    <template v-if="data">
      <!-- Totals -->
      <div class="col-12 g">
        <div class="tiles">
          <div class="card card-body mb-0 tile">
            <small class="text-muted">إجمالي الطلبات</small>
            <h3 class="num mb-0">{{ fmt(t.n) }}</h3>
            <small class="text-muted"><span class="num">{{ perMinute }}</span> طلب/دقيقة في المتوسط</small>
          </div>
          <div class="card card-body mb-0 tile">
            <small class="text-muted">متوسط زمن الاستجابة</small>
            <h3 class="num mb-0" :class="speedClass(avg(t))"><bdi dir="ltr">{{ ms(avg(t)) }}</bdi></h3>
            <small class="text-muted">أبطأ طلب: <span class="num"><bdi dir="ltr">{{ ms(t.mx) }}</bdi></span></small>
          </div>
          <div class="card card-body mb-0 tile">
            <small class="text-muted">طلبات بطيئة (3 ثوانٍ فأكثر)</small>
            <h3 class="num mb-0" :class="{ 'text-warning': t.t4 }">{{ fmt(t.t4) }}</h3>
            <small class="text-muted"><span class="num">{{ pct(t.t4, t.n) }}%</span> من الطلبات</small>
          </div>
          <div class="card card-body mb-0 tile">
            <small class="text-muted">أخطاء السيرفر (5xx) والمقطوعة</small>
            <h3 class="num mb-0" :class="{ 'text-danger': t.e5 + t.ab }">{{ fmt(t.e5 + t.ab) }}</h3>
            <small class="text-muted"><span class="num">{{ fmt(t.e5) }}</span> خطأ · <span class="num">{{ fmt(t.ab) }}</span> انقطع قبل الرد</small>
          </div>
          <div class="card card-body mb-0 tile">
            <small class="text-muted">حجم البيانات المرسلة</small>
            <h3 class="num mb-0"><bdi dir="ltr">{{ size(t.b) }}</bdi></h3>
            <small class="text-muted">متوسط الرد <span class="num"><bdi dir="ltr">{{ size(t.n ? t.b / t.n : 0) }}</bdi></span></small>
          </div>
        </div>
      </div>

      <!-- Timeline -->
      <div class="col-12 g">
        <div class="card mb-0">
          <div class="card-header pb-0">
            <h5 class="mb-0">الطلبات {{ data.hourly ? 'في كل ساعة' : 'في كل يوم' }}</h5>
            <small class="text-muted">الأعمدة: عدد الطلبات · الخط: متوسط زمن الاستجابة</small>
          </div>
          <div class="card-body pt-0">
            <vue-apex-charts type="line" height="280" :options="chartOptions" :series="chartSeries" />
          </div>
        </div>
      </div>

      <!-- Sides / clients / durations -->
      <div class="col-12 col-xl-5 g">
        <div class="card h-100 mb-0">
          <div class="card-header"><h5 class="mb-0">حسب الجهة</h5></div>
          <div class="card-body pt-0">
            <table class="table table-sm mb-0">
              <thead><tr><th>الجهة</th><th>الطلبات</th><th>المتوسط</th><th>بطيئة</th><th>أخطاء</th></tr></thead>
              <tbody>
                <tr v-for="s in sortedSides" :key="s.r" class="clickable" @click="pickSide(s.r)" :title="'عرض ' + sides[s.r] + ' فقط'">
                  <td>
                    <span class="badge" :class="'side-' + s.r">{{ sides[s.r] || s.r }}</span>
                    <div class="bar"><span :style="{ width: pct(s.n, t.n) + '%' }"></span></div>
                  </td>
                  <td class="num">{{ fmt(s.n) }} <small class="text-muted">{{ pct(s.n, t.n) }}%</small></td>
                  <td class="num" :class="speedClass(avg(s))"><bdi dir="ltr">{{ ms(avg(s)) }}</bdi></td>
                  <td class="num">{{ fmt(s.t4) }}</td>
                  <td class="num" :class="{ 'text-danger': s.e5 + s.ab }">{{ fmt(s.e5 + s.ab) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div class="col-12 col-md-6 col-xl-4 g">
        <div class="card h-100 mb-0">
          <div class="card-header"><h5 class="mb-0">من أين تأتي الطلبات</h5></div>
          <div class="card-body pt-0">
            <div v-for="c in clients" :key="c.key" class="meter">
              <div class="d-flex justify-content-between"><span>{{ c.label }}</span><span class="num">{{ fmt(t[c.key]) }} <small class="text-muted">{{ pct(t[c.key], t.n) }}%</small></span></div>
              <div class="bar"><span :style="{ width: pct(t[c.key], t.n) + '%' }"></span></div>
            </div>
            <small class="text-muted">حسب نوع المتصفح أو التطبيق الذي أرسل الطلب.</small>
          </div>
        </div>
      </div>
      <div class="col-12 col-md-6 col-xl-3 g">
        <div class="card h-100 mb-0">
          <div class="card-header"><h5 class="mb-0">توزيع زمن الاستجابة</h5></div>
          <div class="card-body pt-0">
            <div v-for="d in durations" :key="d.key" class="meter">
              <div class="d-flex justify-content-between"><span>{{ d.label }}</span><span class="num">{{ fmt(t[d.key]) }} <small class="text-muted">{{ pct(t[d.key], t.n) }}%</small></span></div>
              <div class="bar" :class="'dur-' + d.key"><span :style="{ width: pct(t[d.key], t.n) + '%' }"></span></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="col-12 g">
        <div class="card mb-0">
          <div class="card-header flex-wrap tabs-head">
            <ul class="nav nav-pills mb-0" role="tablist">
              <li class="nav-item"><a href="#" class="nav-link" :class="{ active: tab == 'routes' }" @click.prevent="tab = 'routes'">المسارات (<span class="num">{{ fmt(data.routes.length) }}</span>)</a></li>
              <li class="nav-item"><a href="#" class="nav-link" :class="{ active: tab == 'slow' }" @click.prevent="openSlow()">الطلبات البطيئة</a></li>
              <li class="nav-item"><a href="#" class="nav-link" :class="{ active: tab == 'schools' }" @click.prevent="openSchools()">أكثر المدارس طلبات</a></li>
            </ul>
            <div v-if="tab == 'routes'" class="d-flex flex-wrap align-items-center tab-tools">
              <select id="rs-sort" class="form-control form-control-sm" v-model="sort" aria-label="ترتيب المسارات">
                <option v-for="s in sorts" :key="s.key" :value="s.key">{{ s.label }}</option>
              </select>
              <input id="rs-search" class="form-control form-control-sm" dir="ltr" v-model.trim="search" placeholder="بحث في المسار، مثل calls/history" aria-label="بحث في المسارات">
            </div>
            <div v-if="tab == 'slow'" class="d-flex align-items-center tab-tools">
              <select id="rs-slow-sort" class="form-control form-control-sm" v-model="slowSort" @change="loadSlow()" aria-label="ترتيب الطلبات البطيئة">
                <option value="ms">الأبطأ أولاً</option>
                <option value="at">الأحدث أولاً</option>
              </select>
            </div>
          </div>

          <!-- Routes -->
          <div v-if="tab == 'routes'" class="table-responsive">
            <table class="table table-sm table-hover mb-0">
              <thead>
                <tr>
                  <th>المسار</th><th>الجهة</th><th>الطلبات</th><th>المتوسط</th><th>الأقصى</th><th>زمن الاستجابة</th><th>متوسط الحجم</th><th>إجمالي الحجم</th><th>بطيئة</th><th>أخطاء</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="x in shownRoutes" :key="x.m + x.p + x.r" class="clickable" @click="setFocus(x)" title="عرض هذا المسار فقط">
                  <td class="path"><bdi dir="ltr" class="mono"><small class="text-muted">{{ x.m }}</small> {{ x.p }}</bdi></td>
                  <td><span class="badge" :class="'side-' + x.r">{{ sides[x.r] || x.r }}</span></td>
                  <td class="num">{{ fmt(x.n) }} <small class="text-muted">{{ pct(x.n, t.n) }}%</small></td>
                  <td class="num" :class="speedClass(avg(x))"><bdi dir="ltr">{{ ms(avg(x)) }}</bdi></td>
                  <td class="num"><bdi dir="ltr">{{ ms(x.mx) }}</bdi></td>
                  <td>
                    <div class="stack" :title="stackTitle(x)">
                      <span v-for="d in durations" :key="d.key" :class="'dur-' + d.key" :style="{ width: pct(x[d.key], x.n) + '%' }"></span>
                    </div>
                  </td>
                  <td class="num"><bdi dir="ltr">{{ size(x.n ? x.b / x.n : 0) }}</bdi></td>
                  <td class="num"><bdi dir="ltr">{{ size(x.b) }}</bdi></td>
                  <td class="num" :class="{ 'text-warning': x.t4 }">{{ fmt(x.t4) }}</td>
                  <td class="num" :class="{ 'text-danger': x.e5 + x.ab }">{{ fmt(x.e5 + x.ab) }}<small v-if="x.e4" class="text-muted"> · 4xx {{ fmt(x.e4) }}</small></td>
                </tr>
                <tr v-if="!shownRoutes.length"><td colspan="10" class="text-center text-muted p-2">لا توجد طلبات في هذه الفترة.</td></tr>
              </tbody>
            </table>
            <div v-if="filteredRoutes.length > limit" class="text-center p-1">
              <button class="btn btn-sm btn-outline-primary" @click="limit += 100">عرض المزيد ({{ fmt(filteredRoutes.length - limit) }})</button>
            </div>
          </div>

          <!-- Slow requests -->
          <div v-if="tab == 'slow'" class="table-responsive">
            <table class="table table-sm table-hover mb-0">
              <thead><tr><th>الوقت</th><th>المسار</th><th>الجهة</th><th>المدرسة</th><th>المدة</th><th>الحالة</th><th>الحجم</th><th>المصدر</th></tr></thead>
              <tbody>
                <tr v-for="(x, i) in slowList" :key="i">
                  <td class="num nowrap">{{ x.at }}</td>
                  <td class="path"><bdi dir="ltr" class="mono"><small class="text-muted">{{ x.m }}</small> {{ x.u }}</bdi></td>
                  <td><span class="badge" :class="'side-' + x.r">{{ sides[x.r] || x.r }}</span></td>
                  <td><small>{{ x.school || '—' }}</small></td>
                  <td class="num text-warning nowrap"><bdi dir="ltr">{{ ms(x.ms) }}</bdi></td>
                  <td class="num"><span v-if="x.st" :class="{ 'text-danger': x.st >= 500 }">{{ x.st }}</span><small v-else class="text-danger">انقطع</small></td>
                  <td class="num"><bdi dir="ltr">{{ size(x.b) }}</bdi></td>
                  <td><small>{{ clientName(x.c) }}</small></td>
                </tr>
                <tr v-if="!slowLoading && !slowList.length"><td colspan="8" class="text-center text-muted p-2">لا توجد طلبات استغرقت 3 ثوانٍ أو أكثر في هذه الفترة.</td></tr>
                <tr v-if="slowLoading"><td colspan="8" class="text-center text-muted p-2">جاري التحميل...</td></tr>
              </tbody>
            </table>
            <small class="text-muted d-block p-1">أحدث 200 طلب بحد أقصى.</small>
          </div>

          <!-- Schools -->
          <div v-if="tab == 'schools'" class="table-responsive">
            <table class="table table-sm table-hover mb-0">
              <thead><tr><th>#</th><th>المدرسة</th><th>الطلبات</th><th>من المدرسة</th><th>من المعلمين</th><th>من أولياء الأمور</th><th>المتوسط</th><th>أخطاء</th></tr></thead>
              <tbody>
                <tr v-for="(x, i) in schoolList" :key="x.s">
                  <td class="num text-muted">{{ i + 1 }}</td>
                  <td>{{ x.name || 'مدرسة محذوفة' }} <small class="text-muted mono d-block" dir="ltr">{{ x.s }}</small></td>
                  <td class="num"><strong>{{ fmt(x.n) }}</strong></td>
                  <td class="num">{{ fmt(x.school) }}</td>
                  <td class="num">{{ fmt(x.teacher) }}</td>
                  <td class="num">{{ fmt(x.parent) }}</td>
                  <td class="num" :class="speedClass(x.n ? x.ms / x.n : 0)"><bdi dir="ltr">{{ ms(x.n ? x.ms / x.n : 0) }}</bdi></td>
                  <td class="num" :class="{ 'text-danger': x.e5 }">{{ fmt(x.e5) }}</td>
                </tr>
                <tr v-if="!schoolsLoading && !schoolList.length"><td colspan="8" class="text-center text-muted p-2">لا توجد بيانات في هذه الفترة.</td></tr>
                <tr v-if="schoolsLoading"><td colspan="8" class="text-center text-muted p-2">جاري التحميل...</td></tr>
              </tbody>
            </table>
            <small class="text-muted d-block p-1">أعلى 100 مدرسة. تُحسب بالأيام الكاملة (من بداية يوم أول الفترة)، ولا يُعرف رقم المدرسة في بعض الطلبات مثل تسجيل الدخول.</small>
          </div>
        </div>
      </div>
    </template>

    <div v-else-if="loading" class="col-12 g text-center text-muted p-3">جاري التحميل...</div>

    <!-- Error log -->
    <div class="col-12 g">
      <div class="card mb-0">
        <div class="card-header flex-wrap errs-head">
          <div>
            <h5 class="mb-25">سجل الأخطاء <small class="text-muted">آخر 7 أيام</small></h5>
            <small class="text-muted">كل خطأ في سطر واحد مع عدد مرات تكراره. اضغط على السطر لعرض التفاصيل.</small>
          </div>
          <div class="d-flex flex-wrap align-items-center errs-tools">
            <select id="rs-err-kind" class="form-control form-control-sm" v-model="errKind" @change="loadErrors()" aria-label="نوع الخطأ">
              <option value="">كل الأنواع</option>
              <option value="http">أخطاء السيرفر (5xx)</option>
              <option value="aborted">انقطع قبل الرد</option>
              <option value="crash">أعطال برمجية</option>
            </select>
            <button class="btn btn-sm btn-outline-primary" @click="loadErrors()" :disabled="errLoading"><i class="fa fa-refresh" :class="{ 'fa-spin': errLoading }"></i></button>
          </div>
        </div>
        <div class="card-body pt-0 pb-50">
          <div class="d-flex flex-wrap chips">
            <button v-for="c in errChips" :key="c.r" class="btn btn-sm" :class="errSide == c.r ? 'btn-primary' : 'btn-outline-secondary'" @click="errSide = c.r; loadErrors()">
              {{ c.label }} <bdi class="num">{{ fmt(c.n) }}</bdi>
            </button>
          </div>
        </div>
        <div class="table-responsive">
          <table class="table table-sm table-hover mb-0">
            <thead><tr><th>آخر مرة</th><th>الجهة</th><th>النوع</th><th>المسار / الرسالة</th><th>التكرار</th><th>أول مرة</th><th>أطول انتظار</th><th>المدرسة</th></tr></thead>
            <tbody>
              <template v-for="(x, i) in errList">
                <tr :key="'e' + i" class="clickable" @click="errOpen = errOpen === i ? null : i">
                  <td class="num nowrap">{{ x.last.slice(5, 16) }}</td>
                  <td><span class="badge" :class="'side-' + x.r">{{ x.r == 'server' ? 'السيرفر' : (sides[x.r] || x.r) }}</span></td>
                  <td class="nowrap">
                    <span v-if="x.k == 'crash'" class="badge badge-light-danger">عطل برمجي</span>
                    <span v-else-if="x.st" class="badge badge-light-danger num">{{ x.st }}</span>
                    <span v-else class="badge badge-light-warning">انقطع قبل الرد</span>
                  </td>
                  <td class="path">
                    <bdi v-if="x.p" dir="ltr" class="mono"><small class="text-muted">{{ x.m }}</small> {{ x.p }}</bdi>
                    <div v-if="x.msg" class="msg"><bdi dir="ltr" class="mono">{{ x.msg }}</bdi></div>
                  </td>
                  <td class="num"><strong>{{ fmt(x.n) }}</strong></td>
                  <td class="num nowrap text-muted">{{ x.first.slice(5, 16) }}</td>
                  <td class="num"><bdi dir="ltr" v-if="x.mx">{{ ms(x.mx) }}</bdi><span v-else>—</span></td>
                  <td><small>{{ x.school || '—' }}</small></td>
                </tr>
                <tr v-if="errOpen === i" :key="'d' + i" class="detail">
                  <td colspan="8">
                    <div v-if="x.u"><small class="text-muted">مثال على الرابط:</small> <bdi dir="ltr" class="mono">{{ x.u }}</bdi></div>
                    <div v-if="!x.st && x.k == 'http'"><small class="text-muted">التطبيق أو المتصفح قطع الاتصال قبل ما السيرفر يرد، غالباً لأن الرد اتأخر (مهلة التطبيقات 15 ثانية) أو المستخدم قفل الصفحة.</small></div>
                    <pre v-if="x.stack" dir="ltr" class="trace">{{ x.stack }}</pre>
                    <small v-if="x.k == 'http' && x.st && !x.msg" class="text-muted">رسالة الخطأ غير معروفة (الرد خرج بخطأ من غير رسالة).</small>
                  </td>
                </tr>
              </template>
              <tr v-if="!errLoading && !errList.length"><td colspan="8" class="text-center text-muted p-2">لا توجد أخطاء في آخر 7 أيام 🎉</td></tr>
              <tr v-if="errLoading && !errList.length"><td colspan="8" class="text-center text-muted p-2">جاري التحميل...</td></tr>
            </tbody>
          </table>
        </div>
        <small class="text-muted d-block p-1">أحدث 300 خطأ مختلف. أخطاء التحقق العادية (مثل «بيانات الدخول غير صحيحة») ليست أخطاء ولا تظهر هنا.</small>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import RsGauge from './request-stats-gauge.vue'

var DAY = 86400000;
function riyadhDate(t) { return new Date(t + 3 * 3600000).toISOString().slice(0, 10); }

export default {
  components: { VueApexCharts, RsGauge },
  data() {
    var today = riyadhDate(Date.now());
    return {
      jwt: localStorage.getItem('jwt'),
      range: '24h',
      from: today,
      to: today,
      today: today,
      minDate: riyadhDate(Date.now() - 30 * DAY),
      side: '',
      focus: null,
      data: null,
      loading: false,
      updatedAt: '',
      auto: false,
      timer: null,
      tab: 'routes',
      sort: 'n',
      search: '',
      limit: 100,
      slowList: [],
      slowSort: 'ms',
      slowLoading: false,
      schoolList: [],
      schoolsLoading: false,
      live: null,
      peak: 0,
      errList: [],
      errSides: [],
      errSide: '',
      errKind: '',
      errOpen: null,
      errLoading: false,
      liveTimer: null,
      liveBusy: false,
      narrow: window.innerWidth < 768,
      zonesRps: [
        { from: 0, to: 60, color: '#2ee6a6' },
        { from: 60, to: 90, color: '#ffb020' },
        { from: 90, to: 120, color: '#ff4d4f' },
      ],
      zonesPct: [
        { from: 0, to: 70, color: '#2ee6a6' },
        { from: 70, to: 85, color: '#ffb020' },
        { from: 85, to: 100, color: '#ff4d4f' },
      ],
      ranges: [
        { key: '24h', label: 'آخر 24 ساعة' },
        { key: 'today', label: 'اليوم' },
        { key: 'yesterday', label: 'أمس' },
        { key: '7d', label: '7 أيام' },
        { key: '30d', label: '30 يوماً' },
        { key: 'custom', label: 'تاريخ محدد' },
      ],
      sides: {
        school: 'المدرسة',
        teacher: 'المعلم',
        parent: 'ولي الأمر',
        admin: 'الإدارة',
        reseller: 'الشركاء',
        files: 'الملفات',
        general: 'عام',
      },
      clients: [
        { key: 'cw', label: 'المتصفح (لوحة المدرسة والإدارة)' },
        { key: 'cf', label: 'التطبيقات الجديدة' },
        { key: 'ce', label: 'التطبيقات القديمة (Expo)' },
        { key: 'co', label: 'أخرى (أجهزة، خوادم، سكربتات)' },
      ],
      durations: [
        { key: 't1', label: 'أقل من 200ms' },
        { key: 't2', label: '200ms – 1 ثانية' },
        { key: 't3', label: '1 – 3 ثوانٍ' },
        { key: 't4', label: '3 ثوانٍ فأكثر' },
      ],
      sorts: [
        { key: 'n', label: 'الأكثر طلباً' },
        { key: 'avg', label: 'الأبطأ (المتوسط)' },
        { key: 'mx', label: 'أعلى زمن لطلب واحد' },
        { key: 'ms', label: 'الأكثر استهلاكاً لوقت السيرفر' },
        { key: 'size', label: 'الأكبر حجماً (متوسط الرد)' },
        { key: 'b', label: 'الأكثر بيانات (إجمالي)' },
        { key: 'err', label: 'الأكثر أخطاء' },
        { key: 't4', label: 'الأكثر طلبات بطيئة' },
      ],
    };
  },
  computed: {
    t() { return this.data ? this.data.totals : {}; },
    // CPU use from the 1-minute load average the server sends: load ÷ cores
    cpuPct() {
      if (!this.live) return 0;
      return Math.max(0, Math.min(100, Math.round((this.live.load / Math.max(1, this.live.cpus)) * 100)));
    },
    ramPct() { return this.live && this.live.ram.total ? Math.round((this.live.ram.used / this.live.ram.total) * 100) : 0; },
    diskPct() {
      var d = this.live && this.live.disk;
      return d && d.used + d.free ? Math.round((d.used / (d.used + d.free)) * 100) : 0;
    },
    rpsMood() {
      var v = this.live.rps;
      return v < 1 ? 'نايم 😴' : v < 15 ? 'على رواقة 🚶' : v < 40 ? 'ماشي 🚗' : v < 80 ? 'زحمة 🏎️' : 'ولّعت 🔥';
    },
    errChips() {
      var g = this, by = {}, total = 0;
      this.errSides.forEach(function(x) { by[x.r] = x.n; total += x.n; });
      var order = ['server', 'school', 'teacher', 'parent', 'admin', 'reseller', 'files', 'general'];
      return [{ r: '', label: 'الكل', n: total }].concat(order.filter(function(r) { return by[r]; }).map(function(r) {
        return { r: r, label: r == 'server' ? 'أعطال السيرفر' : g.sides[r], n: by[r] };
      }));
    },
    ramMood() { var v = this.ramPct; return v < 70 ? 'رايقة 🧊' : v < 85 ? 'سخنت شوية 🌡️' : 'بتغلي 🥵'; },
    diskMood() { var v = this.diskPct; return v < 70 ? 'التانك فاضي كفاية ⛽' : v < 85 ? 'خلّي بالك 👀' : 'قرب يتملي 🚨'; },
    perMinute() {
      if (!this.data || !this.data.timeline.length) return 0;
      var minutes = this.data.timeline.length * (this.data.hourly ? 60 : 1440);
      // the current hour/day is not over yet
      var start = new Date(this.data.from.replace(' ', 'T') + '+03:00').getTime();
      minutes = Math.max(1, Math.min(minutes, (Date.now() - start) / 60000));
      return this.fmt(Math.round(this.t.n / minutes));
    },
    sortedSides() {
      return (this.data ? this.data.sides : []).slice().sort(function(a, b) { return b.n - a.n; });
    },
    filteredRoutes() {
      var g = this, q = this.search.toLowerCase();
      var list = this.data ? this.data.routes.filter(function(x) { return !q || x.p.toLowerCase().includes(q); }) : [];
      var key = {
        n: function(x) { return x.n; },
        avg: function(x) { return x.n >= 5 ? g.avg(x) : g.avg(x) / 1e6; }, // a route seen 1-4 times can't top this list
        mx: function(x) { return x.mx; },
        ms: function(x) { return x.ms; },
        size: function(x) { return x.n >= 5 ? x.b / x.n : x.b / x.n / 1e6; },
        b: function(x) { return x.b; },
        err: function(x) { return x.e5 + x.ab + x.e4 / 1000; },
        t4: function(x) { return x.t4; },
      }[this.sort];
      return list.sort(function(a, b) { return key(b) - key(a); });
    },
    shownRoutes() { return this.filteredRoutes.slice(0, this.limit); },
    chartSeries() {
      var tl = this.data ? this.data.timeline : [];
      return [
        { name: 'الطلبات', type: 'column', data: tl.map(function(x) { return x.n; }) },
        { name: 'متوسط الزمن (ms)', type: 'line', data: tl.map(function(x) { return x.n ? Math.round(x.ms / x.n) : 0; }) },
      ];
    },
    chartOptions() {
      var g = this, tl = this.data ? this.data.timeline : [], hourly = this.data && this.data.hourly;
      var narrow = window.innerWidth < 600;
      var every = Math.max(1, Math.ceil(tl.length / (narrow ? 3 : 12)));
      return {
        chart: { toolbar: { show: false }, zoom: { enabled: false }, fontFamily: 'inherit', animations: { enabled: false } },
        colors: ['#7367f0', '#ff9f43'],
        stroke: { width: [0, 2], curve: 'smooth' },
        plotOptions: { bar: { columnWidth: '70%', borderRadius: 2 } },
        dataLabels: { enabled: false },
        legend: { position: 'top', horizontalAlign: 'left' },
        grid: { borderColor: 'rgba(130,130,150,0.18)', strokeDashArray: 3 },
        xaxis: {
          // a label on every k-th bar only, so they never overlap (tooltips still show each bar's time)
          categories: tl.map(function(x, i) { return i % every ? '' : (hourly ? x.t.slice(11, 16) : x.t.slice(5)); }),
          labels: { rotate: 0, hideOverlappingLabels: false },
          tooltip: { enabled: false },
        },
        yaxis: [
          { title: { text: narrow ? '' : 'الطلبات' }, labels: { formatter: function(v) { return g.fmt(Math.round(v)); } } },
          { opposite: true, title: { text: narrow ? '' : 'ms' }, labels: { formatter: function(v) { return g.fmt(Math.round(v)); } } },
        ],
        tooltip: {
          shared: true,
          x: { formatter: function(v, o) { var p = tl[o.dataPointIndex]; return p ? p.t : v; } },
          y: { formatter: function(v, o) { return o.seriesIndex == 1 ? g.ms(v) : g.fmt(v) + ' طلب'; } },
        },
      };
    },
  },
  watch: {
    auto(on) {
      clearInterval(this.timer);
      if (on) this.timer = setInterval(() => { if (!this.loading && !document.hidden) this.load(true); }, 60000);
    },
    sort() { this.limit = 100; },
    search() { this.limit = 100; },
  },
  created() {
    this.load();
    this.loadLive();
    this.loadErrors();
    this.liveTimer = setInterval(() => { if (!document.hidden) this.loadLive(); }, 2000);
    window.addEventListener('resize', this.onResize);
  },
  beforeDestroy() {
    clearInterval(this.timer);
    clearInterval(this.liveTimer);
    window.removeEventListener('resize', this.onResize);
  },
  methods: {
    body(extra) {
      var b = { jwt: this.jwt, range: this.range, from: this.from, to: this.to, r: this.side };
      if (this.focus) { b.m = this.focus.m; b.p = this.focus.p; }
      return Object.assign(b, extra || {});
    },
    load(quiet) {
      var g = this;
      g.loading = true;
      $.post(api + '/admin/requests/stats', this.body()).then(function(r) {
        g.loading = false;
        if (r.status != 100) return alert(r.response, 200);
        g.data = r.response;
        g.updatedAt = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
        if (g.tab == 'slow') g.loadSlow();
        if (g.tab == 'schools') g.loadSchools();
      }).fail(function() {
        g.loading = false;
        if (!quiet) alert('تعذر الاتصال بالسيرفر', 200);
      });
    },
    loadSlow() {
      var g = this;
      g.slowLoading = true;
      $.post(api + '/admin/requests/slow', this.body({ sort: this.slowSort })).then(function(r) {
        g.slowLoading = false;
        if (r.status != 100) return alert(r.response, 200);
        g.slowList = r.response;
      }).fail(function() { g.slowLoading = false; });
    },
    loadSchools() {
      var g = this;
      g.schoolsLoading = true;
      $.post(api + '/admin/requests/schools', this.body()).then(function(r) {
        g.schoolsLoading = false;
        if (r.status != 100) return alert(r.response, 200);
        g.schoolList = r.response;
      }).fail(function() { g.schoolsLoading = false; });
    },
    loadLive() {
      var g = this;
      if (g.liveBusy) return;
      g.liveBusy = true;
      $.post(api + '/admin/requests/live', { jwt: this.jwt }).then(function(r) {
        g.liveBusy = false;
        if (r.status != 100) return;
        g.live = r.response;
        g.peak = Math.max(g.peak, r.response.rps);
      }).fail(function() { g.liveBusy = false; });
    },
    loadErrors() {
      var g = this;
      g.errLoading = true;
      g.errOpen = null;
      $.post(api + '/admin/requests/errors', { jwt: this.jwt, r: this.errSide, k: this.errKind }).then(function(r) {
        g.errLoading = false;
        if (r.status != 100) return alert(r.response, 200);
        g.errList = r.response.list;
        g.errSides = r.response.sides;
      }).fail(function() { g.errLoading = false; });
    },
    pad(n, len) { var s = String(Math.max(0, Math.round(n || 0))); while (s.length < len) s = '0' + s; return s; },
    onResize() { this.narrow = window.innerWidth < 768; },
    // the side dials turn toward the middle, like a car's instrument cluster
    tilt(side) { return this.narrow ? {} : { transform: 'perspective(900px) rotateY(' + side * 16 + 'deg)' }; },
    gb(b) { b = (b || 0) / 1073741824; return b < 10 ? b.toFixed(1) : Math.round(b).toString(); },
    rpsText(v) { v = v || 0; return v < 10 ? v.toFixed(1) : Math.round(v).toString(); },
    openSlow() { this.tab = 'slow'; this.loadSlow(); },
    openSchools() { this.tab = 'schools'; this.loadSchools(); },
    setRange(key) {
      this.range = key;
      this.load();
    },
    pickSide(r) {
      this.side = this.side == r ? '' : r;
      this.load();
    },
    setFocus(x) {
      this.focus = x ? { m: x.m, p: x.p } : null;
      this.load();
    },
    avg(x) { return x && x.n ? x.ms / x.n : 0; },
    pct(a, b) { return b ? Math.round((a / b) * 1000) / 10 : 0; },
    fmt(n) { return Math.round(n || 0).toLocaleString('en-US'); },
    ms(v) {
      v = Math.round(v || 0);
      return v < 1000 ? v + ' ms' : (v / 1000).toFixed(v < 10000 ? 2 : 1) + ' s';
    },
    size(b) {
      b = b || 0;
      if (b < 1024) return Math.round(b) + ' B';
      if (b < 1048576) return (b / 1024).toFixed(1) + ' KB';
      if (b < 1073741824) return (b / 1048576).toFixed(1) + ' MB';
      return (b / 1073741824).toFixed(2) + ' GB';
    },
    speedClass(v) { return v >= 3000 ? 'text-danger' : v >= 1000 ? 'text-warning' : ''; },
    clientName(c) {
      var x = this.clients.find(function(y) { return y.key == 'c' + c; });
      return x ? x.label : '';
    },
    stackTitle(x) {
      var g = this;
      return this.durations.map(function(d) { return d.label + ': ' + g.fmt(x[d.key]); }).join('\n');
    },
  },
};
</script>

<style scoped>
.head { gap: 12px; }
.tools, .filters, .dates, .tab-tools { gap: 8px; }
.tools .custom-switch { margin-left: 4px; }
.filters select { min-width: 150px; }
.focus { display: inline-flex; align-items: center; gap: 6px; padding: 6px 10px; font-size: 0.9rem; white-space: normal; }
.mono { font-family: Menlo, Consolas, 'Courier New', monospace; font-size: 0.85rem; }
.num { font-variant-numeric: tabular-nums; }
.nowrap { white-space: nowrap; }
.tiles { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 14px; }
.tile h3 { margin-top: 4px; }
.bar { height: 6px; border-radius: 3px; background: rgba(130, 130, 150, 0.15); overflow: hidden; margin-top: 4px; }
.bar span { display: block; height: 100%; background: #7367f0; border-radius: 3px; }
.meter { margin-bottom: 12px; }
.dur-t1 span, span.dur-t1 { background: #28c76f; }
.dur-t2 span, span.dur-t2 { background: #00cfe8; }
.dur-t3 span, span.dur-t3 { background: #ff9f43; }
.dur-t4 span, span.dur-t4 { background: #ea5455; }
.stack { display: flex; width: 110px; height: 8px; border-radius: 4px; overflow: hidden; background: rgba(130, 130, 150, 0.15); }
.stack span { display: block; height: 100%; }
.path { min-width: 230px; max-width: 380px; word-break: break-all; }
.clickable { cursor: pointer; }
.tabs-head { gap: 10px; }
.tab-tools input { min-width: 240px; }
.side-school { background: #7367f0; color: #fff; }
.side-teacher { background: #00cfe8; color: #fff; }
.side-parent { background: #28c76f; color: #fff; }
.side-admin { background: #4b4b4b; color: #fff; }
.side-reseller { background: #ff9f43; color: #fff; }
.side-files { background: #82868b; color: #fff; }
.side-general { background: #b4b7bd; color: #222; }
th { white-space: nowrap; }
.errs-head { gap: 10px; }
.errs-tools { gap: 8px; }
.chips { gap: 6px; }
.msg { margin-top: 2px; color: #ea5455; }
.detail td { background: rgba(130, 130, 150, 0.06); }
.trace { margin: 6px 0 0; padding: 8px 10px; background: #1e2128; color: #e6e9ef; border-radius: 6px; font-size: 0.78rem; white-space: pre-wrap; word-break: break-all; max-height: 220px; overflow: auto; }
.side-server { background: #ea5455; color: #fff; }
.cluster {
  border-radius: 22px;
  padding: 16px 20px 18px;
  background: linear-gradient(180deg, #f7f8fa 0%, #e6e9ee 100%);
  box-shadow: inset 0 1px 0 #ffffff, 0 8px 24px rgba(30, 36, 48, 0.12);
  border: 1px solid #dde1e7;
}
.cluster-head { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 14px; margin-bottom: 8px; }
.cluster-head h5 { color: #2f343c; }
.cpu { display: inline-flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.cpu-label { font-size: 0.85rem; font-weight: 600; color: #4a515c; }
.cpu-bar { width: 200px; max-width: 46vw; height: 10px; border-radius: 6px; background: #d9dde3; box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.18); overflow: hidden; }
.cpu-bar span { display: block; height: 100%; border-radius: 6px; background: linear-gradient(180deg, #4fdc8f, #28c76f); transition: width 0.8s ease; }
.cpu-bar span.warm { background: linear-gradient(180deg, #ffbd6b, #ff9f43); }
.cpu-bar span.hot { background: linear-gradient(180deg, #f37b7c, #ea5455); }
.cpu-val { font-weight: 700; color: #2f343c; min-width: 38px; }
@media (prefers-reduced-motion: reduce) {
  .cpu-bar span { transition: none; }
}
.live { display: inline-flex; align-items: center; gap: 7px; font-size: 0.82rem; font-weight: 600; color: #7a818c; background: #ffffff; border: 1px solid #dde1e7; border-radius: 20px; padding: 2px 10px; }
.live i { width: 8px; height: 8px; border-radius: 50%; background: #a3a9b2; display: inline-block; }
.live.on { color: #1b9e5a; }
.live.on i { background: #28c76f; box-shadow: 0 0 6px #28c76f; animation: blink 1.4s ease-in-out infinite; }
@keyframes blink { 50% { opacity: 0.3; } }
.dials { display: grid; grid-template-columns: 1fr 1.32fr 1fr; align-items: center; gap: 10px; }
.dial-side { max-width: 270px; width: 100%; justify-self: center; }
.dial-main { max-width: 360px; width: 100%; justify-self: center; }
@media (max-width: 767px) {
  .dials { grid-template-columns: 1fr; }
  .dial-main { order: -1; max-width: 320px; }
  .dial-side { max-width: 260px; }
}
@media (prefers-reduced-motion: reduce) {
  .live.on i { animation: none; }
}
</style>
